import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import { ReportServices } from "../../services/reports/reports";

// Escape special regex characters so user input never breaks RegExp construction
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Reusable user-search autocomplete component.
 *
 * Props:
 *   onSelect(user)   — called when the user picks a suggestion
 *   onClear()        — called when the user edits the input after a selection
 *   value            — controlled display value (string)
 *   error            — validation error message string
 *   placeholder      — optional input placeholder
 */
const UserSearchSelect = ({ onSelect, onClear, value = "", error, placeholder }) => {
    const { t } = useTranslation();

    const [inputValue,   setInputValue]   = useState(value);
    const [results,      setResults]      = useState([]);
    const [isSearching,  setIsSearching]  = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searched,     setSearched]     = useState(false); // did we complete at least one search?

    const dropdownRef   = useRef(null);
    const searchTimeout = useRef(null);
    // Race-condition guard: only the latest in-flight request may update state
    const requestId     = useRef(0);

    // Keep the display value in sync when the parent resets the form
    useEffect(() => {
        setInputValue(value);
        if (!value) {
            setResults([]);
            setShowDropdown(false);
            setSearched(false);
        }
    }, [value]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const performSearch = async (term) => {
        if (!term || term.length < 1) {
            setResults([]);
            setShowDropdown(false);
            setSearched(false);
            return;
        }

        // Assign a new request ID so stale responses can be ignored
        const currentRequest = ++requestId.current;

        setIsSearching(true);
        try {
            // ReportServices.searchUsers returns the unwrapped array directly
            const response = await ReportServices.searchUsers(term);

            // Ignore responses that arrived out of order
            if (currentRequest !== requestId.current) return;

            const list = Array.isArray(response) ? response : [];
            setResults(list);
            setShowDropdown(true); // show even if empty so "no results" message appears
            setSearched(true);
        } catch {
            if (currentRequest !== requestId.current) return;
            setResults([]);
            setShowDropdown(false);
        } finally {
            if (currentRequest === requestId.current) {
                setIsSearching(false);
            }
        }
    };

    const handleChange = (e) => {
        const val = e.target.value;
        setInputValue(val);

        // If the user edits after having selected someone, clear the selection
        if (onClear) onClear();

        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => performSearch(val), 300);
    };

    const handleSelect = (user) => {
        setInputValue(user.username);
        setResults([]);
        setShowDropdown(false);
        if (onSelect) onSelect(user);
    };

    // Highlight matching substring safely (no stateful regex, no SyntaxError)
    const highlightMatch = (text, query) => {
        if (!query || !text) return text;
        const parts = text.split(new RegExp(`(${escapeRegex(query)})`, "gi"));
        return parts.map((part, i) =>
            part.toLowerCase() === query.toLowerCase()
                ? <strong key={i}>{part}</strong>
                : part
        );
    };

    return (
        <div className="position-relative" ref={dropdownRef}>
            <div className="position-relative">
                <input
                    type="text"
                    className={`form-control${error ? " is-invalid" : ""}`}
                    placeholder={placeholder || t("search_user_placeholder")}
                    value={inputValue}
                    onChange={handleChange}
                    onFocus={() => results.length > 0 && setShowDropdown(true)}
                    autoComplete="off"
                />
                {isSearching && (
                    <Spinner
                        animation="border"
                        size="sm"
                        className="position-absolute"
                        style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                    />
                )}
            </div>

            {error && (
                <span role="alert" className="error-message-validator">
                    {error}
                </span>
            )}

            {showDropdown && (
                <div
                    style={{
                        position:        "absolute",
                        top:             "100%",
                        left:            0,
                        right:           0,
                        backgroundColor: "#fff",
                        border:          "1px solid #ddd",
                        borderRadius:    "4px",
                        boxShadow:       "0 4px 6px rgba(0,0,0,0.1)",
                        zIndex:          1000,
                        maxHeight:       "250px",
                        overflowY:       "auto",
                    }}
                >
                    {results.length === 0 && searched && !isSearching ? (
                        <div style={{ padding: "10px 15px", color: "#888", fontSize: "14px" }}>
                            {t("sorry_no_data_found")}
                        </div>
                    ) : (
                        results.map((user) => (
                            <div
                                key={user.id}
                                onClick={() => handleSelect(user)}
                                style={{
                                    padding:      "10px 15px",
                                    cursor:       "pointer",
                                    borderBottom: "1px solid #eee",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                            >
                                <div style={{ fontWeight: "bold", color: "#333" }}>
                                    {highlightMatch(user.username, inputValue)}
                                </div>
                                {user.fullName && (
                                    <div style={{ fontSize: "12px", color: "#666" }}>
                                        {highlightMatch(user.fullName, inputValue)}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default UserSearchSelect;
